import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as connectionAPI from './connectionAPI';

// Initial state
const initialState = {
  loading: false,
  error: null,
  pendingConnections: [],
  approvedConnections: [],
  connectionDetails: null,
  allUsers: [],
  filteredConnections: [],
  selectedConnection: null,
  filters: {
    username: '',
    city: '',
    sortBy: 'date'
  }
};

// Async thunks
export const sendConnectionAsync = createAsyncThunk(
  'connections/sendConnection',
  async (username) => {
    const response = await connectionAPI.sendConnection(username);
    return response;
  }
);

export const acceptConnectionAsync = createAsyncThunk(
  'connections/acceptConnection',
  async (username) => {
    const response = await connectionAPI.acceptConnection(username);
    return { username, ...response };
  }
);

export const rejectConnectionAsync = createAsyncThunk(
  'connections/rejectConnection',
  async (username) => {
    const response = await connectionAPI.rejectConnection(username);
    return { username, ...response };
  }
);

export const getConnectionDetailsAsync = createAsyncThunk(
  'connections/getDetails',
  async (username) => {
    const response = await connectionAPI.getConnectionDetails(username);
    return { username, ...response };
  }
);

export const getAllUsersByRoleAsync = createAsyncThunk(
  'connections/getAllUsers',
  async () => {
    const response = await connectionAPI.getAllUsersByRole();
    return response;
  }
);

export const getConnectionsByTypeAndStatusAsync = createAsyncThunk(
  'connections/getConnectionsByTypeAndStatus',
  async ({ type, status }) => {
    const response = await connectionAPI.getConnectionsByTypeAndStatus(type, status);
    return response;
  }
);

// Slice
const connectionSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetConnectionDetails: (state) => {
      state.connectionDetails = null;
    },
    clearFilteredConnections: (state) => {
      state.filteredConnections = [];
    },
    setSelectedConnection: (state, action) => {
      state.selectedConnection = action.payload;
    },
    clearSelectedConnection: (state) => {
      state.selectedConnection = null;
    },
    setFilterUsername: (state, action) => {
      state.filters.username = action.payload;
    },
    setFilterCity: (state, action) => {
      state.filters.city = action.payload;
    },
    setSortBy: (state, action) => {
      state.filters.sortBy = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Send Connection
      .addCase(sendConnectionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendConnectionAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingConnections.push({
          username: action.meta.arg,
          status: 'PENDING'
        });
      })
      .addCase(sendConnectionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Accept Connection
      .addCase(acceptConnectionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptConnectionAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from pending and add to approved
        state.pendingConnections = state.pendingConnections.filter(
          conn => conn.username !== action.payload.username
        );
        state.approvedConnections.push({
          username: action.payload.username,
          status: 'APPROVED'
        });
      })
      .addCase(acceptConnectionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Reject Connection
      .addCase(rejectConnectionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectConnectionAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from pending connections
        state.pendingConnections = state.pendingConnections.filter(
          conn => conn.username !== action.payload.username
        );
      })
      .addCase(rejectConnectionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get Connection Details
      .addCase(getConnectionDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConnectionDetailsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.connectionDetails = {
          username: action.payload.username,
          type: action.payload.type,
          status: action.payload.status
        };
      })
      .addCase(getConnectionDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get All Users
      .addCase(getAllUsersByRoleAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsersByRoleAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsers = action.payload;
      })
      .addCase(getAllUsersByRoleAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get Connections by Type and Status
      .addCase(getConnectionsByTypeAndStatusAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConnectionsByTypeAndStatusAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.filteredConnections = action.payload;
        // Update pending/approved connections based on type
        if (action.meta.arg.status.toUpperCase() === 'PENDING') {
          state.pendingConnections = action.payload;
        } else if (action.meta.arg.status.toUpperCase() === 'APPROVED') {
          state.approvedConnections = action.payload;
        }
        
      })
      .addCase(getConnectionsByTypeAndStatusAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

// Export actions and reducer
export const {
  clearError,
  resetConnectionDetails,
  clearFilteredConnections,
  setSelectedConnection,
  clearSelectedConnection,
  setFilterUsername,
  setFilterCity,
  setSortBy
} = connectionSlice.actions;

export default connectionSlice.reducer;

// Selectors
export const selectLoading = (state) => state.connections.loading;
export const selectError = (state) => state.connections.error;
export const selectPendingConnections = (state) => state.connections.pendingConnections;
export const selectApprovedConnections = (state) => state.connections.approvedConnections;
export const selectConnectionDetails = (state) => state.connections.connectionDetails;
export const selectAllUsers = (state) => state.connections.allUsers;
export const selectFilteredConnections = (state) => state.connections.filteredConnections;
export const selectSelectedConnection = (state) => state.connections.selectedConnection;
export const selectFilters = (state) => state.connections.filters;